"use client";

// SalesPage como ilha client. Recebe `machines` por prop (snapshot
// inicial vindo do Server Component). Lê status/tipo iniciais da URL
// e mantém estado local pra os toggles de filtro.

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { ALL_FILTER, displayRef } from "../lib/mbm-helpers";
import { withLocale } from "../lib/i18n";
import { mapToPtPath } from "../lib/route-map";
import { truncate } from "../lib/text";
import { useLangContext } from "../lib/lang-context";
import { ProductMedia } from "./mbm-components";

function SalesEmptyState({ t, lang, hasMachines }) {
  const message = hasMachines ? t("sales_empty_filter") : t("sales_empty");
  return (
    <div
      style={{
        border: "1px solid var(--rule)",
        padding: 48,
        textAlign: "center",
        maxWidth: 640,
        margin: "32px auto",
      }}
    >
      <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 17 }}>
        {message}
      </p>
      <div style={{ marginTop: 24 }}>
        <Link className="cta-btn" href={withLocale(mapToPtPath("/contact"), lang)}>
          {t("catalog_empty_cta")} <span className="arr" aria-hidden="true">→</span>
        </Link>
      </div>
    </div>
  );
}

export default function SalesPage({ machines, lang }) {
  const { t } = useLangContext();
  const machinesList = Array.isArray(machines) ? machines : [];

  const searchParams = useSearchParams();
  const initialStatus = searchParams?.get("status") || ALL_FILTER;
  const initialType = searchParams?.get("tipo") || ALL_FILTER;
  const [status, setStatus] = useState(initialStatus);
  const [type, setType] = useState(initialType);
  const types = Array.from(new Set(machinesList.map((m) => m.type).filter(Boolean)));

  const filtered = machinesList.filter((s) => {
    const okStatus = status === ALL_FILTER || s.status === status;
    const okType = type === ALL_FILTER || s.type === type;
    return okStatus && okType;
  });

  const statusLabel = (s) => {
    if (s === "available") return t("sales_status_available");
    if (s === "reserved") return t("sales_status_reserved");
    if (s === "new") return t("sales_status_new");
    if (s === "sold") return t("sales_status_sold");
    return s;
  };
  const statusColor = (s) => {
    if (s === "available") return "var(--brand)";
    if (s === "reserved") return "var(--mute)";
    if (s === "new") return "var(--accent)";
    if (s === "sold") return "var(--ink-3)";
    return "var(--ink)";
  };

  return (
    <div className="page">
      <section className="hero shell">
        <h1 style={{ fontSize: "clamp(48px, 7vw, 96px)" }}>
          {t("sec_sales_t_a")} {t("sec_sales_t_b")}
        </h1>
        <p className="sub" style={{ maxWidth: "64ch" }}>{t("sales_intro")}</p>
      </section>

      <section className="section tight shell">
        <div className="cat-controls">
          <div className="group">
            <span className="group-label">{t("sales_filter_status")}</span>
            {[
              { v: ALL_FILTER, l: t("all") },
              { v: "available", l: t("sales_status_available") },
              { v: "reserved", l: t("sales_status_reserved") },
              { v: "new", l: t("sales_status_new") },
              { v: "sold", l: t("sales_status_sold") },
            ].map((o) => (
              <button key={o.v} className={"chip " + (status === o.v ? "on" : "")} onClick={() => setStatus(o.v)}>{o.l}</button>
            ))}
          </div>
          <div className="group">
            <span className="group-label">{t("sales_filter_type")}</span>
            <button className={"chip " + (type === ALL_FILTER ? "on" : "")} onClick={() => setType(ALL_FILTER)}>{t("all")}</button>
            {types.map((c) => (
              <button key={c} className={"chip " + (type === c ? "on" : "")} onClick={() => setType(c)}>{c}</button>
            ))}
          </div>
        </div>

        <div className="label-num" style={{ marginBottom: 16 }}>
          {t("showing")} {filtered.length} {t("of")} {machinesList.length} {t("items")}
        </div>

        {filtered.length === 0 ? (
          <SalesEmptyState
            t={t}
            lang={lang}
            hasMachines={machinesList.length > 0}
          />
        ) : (
          <div className="cards-grid cols-2">
            {filtered.map((p) => (
              <div key={p.id ?? p.ref} className="cat-card" style={{ cursor: "default" }}>
                <ProductMedia product={p} kind="stripes" />
                <div className="body" style={{ padding: "20px 22px 24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                    <div className="ref">{[displayRef(p.ref), p.brand].filter(Boolean).join(" · ")}</div>
                    <span className="mono" style={{
                      fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
                      color: statusColor(p.status),
                      border: `1px solid ${statusColor(p.status)}`,
                      padding: "3px 8px",
                    }}>
                      {statusLabel(p.status)}
                    </span>
                  </div>
                  <h3 style={{ fontSize: 26 }}>{p.name[lang]}</h3>
                  {p.desc[lang] && <p>{truncate(p.desc[lang])}</p>}
                  <div style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px",
                    marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--rule)",
                    fontFamily: "var(--mono)", fontSize: 11,
                  }}>
                    <div><span style={{ color: "var(--mute)" }}>{String(t("sales_capacity")).toUpperCase()}</span><br/>{p.capacity}</div>
                    <div><span style={{ color: "var(--mute)" }}>{String(t("sales_bed")).toUpperCase()}</span><br/>{p.bed}</div>
                    <div><span style={{ color: "var(--mute)" }}>{String(t("sales_year")).toUpperCase()}</span><br/>{p.year}{p.refurb ? ` / ${p.refurb}` : ""}</div>
                    <div><span style={{ color: "var(--mute)" }}>{String(t("sales_spm")).toUpperCase()}</span><br/>{p.spm}</div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
                    <div>
                      <div className="label-num">{t("sales_price")}</div>
                      <div className="h-display" style={{ fontSize: 20, marginTop: 2 }}>{p.price[lang]}</div>
                    </div>
                    <Link
                      className="cta-btn"
                      href={withLocale(
                        mapToPtPath(p.id ? `/contact?interest=machine:${p.id}` : "/contact"),
                        lang
                      )}
                    >
                      {t("sales_inquire")} <span className="arr" aria-hidden="true">→</span>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
